import useNetwork from '@/composables/useNetwork';
import QUERY_KEYS from '@/constants/queryKeys';
import { getWeb3Provider } from '@/dependencies/wallets/Web3Provider';
import { BEPOLIA, BERACHAIN } from '@/providers/appkit.provider';
import { JsonRpcProvider } from '@ethersproject/providers';
import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
  useAppKitTheme,
  useDisconnect,
  useWalletInfo,
} from '@reown/appkit/vue';
import { useQuery } from '@tanstack/vue-query';
import { configService } from '../config/config.service';
import { rpcProviderService } from '../rpc-provider/rpc-provider.service';
import { walletService } from './wallet.service';

/** STATE */
const blockNumber = ref(0);

/** MUTATIONS */
function setBlockNumber(n: number): void {
  blockNumber.value = n;
}

/** INIT STATE */
rpcProviderService.initBlockListener(setBlockNumber);

export default function useWeb3() {
  const { open: connectWallet } = useAppKit();
  const { disconnect: disconnectWallet } = useDisconnect();
  const accountData = useAppKitAccount();
  const networkData = useAppKitNetwork();
  const walletInfoHook = useWalletInfo();
  const walletInfo = computed(() => walletInfoHook.walletInfo);
  const providerHook = useAppKitProvider<JsonRpcProvider>('eip155');
  const walletProvider = computed(() => providerHook.walletProvider);
  const { setThemeMode, setThemeVariables } = useAppKitTheme();

  setThemeMode('light');
  setThemeVariables({
    '--w3m-accent': '#ff7707',
    '--w3m-color-mix': '#f3fce6',
    '--w3m-color-mix-strength': 20,
  });

  const account = computed(() => accountData.value.address ?? '');
  const walletState = computed(() => accountData.value.status);
  const chainId = computed(() =>
    networkData.value.chainId ? Number(networkData.value.chainId) : 80094
  );
  const provider = computed(
    () =>
      walletProvider.value ?? rpcProviderService.getJsonProvider(chainId.value)
  );
  const signer = computed(() => provider.value?.getSigner());

  const appNetworkConfig = configService.network;

  const { networkId } = useNetwork();

  // COMPUTED REFS + COMPUTED REFS
  const userNetworkConfig = computed(() => {
    try {
      if (chainId.value) return configService.getNetworkConfig(chainId.value);
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  });
  const isWalletReady = computed(() => walletState.value === 'connected');
  const isWalletConnecting = computed(() => walletState.value === 'connecting');
  const isWalletDisconnected = computed(
    () => walletState.value === 'disconnected'
  );

  const canLoadProfile = computed(
    () => account.value !== '' && userNetworkConfig.value !== null
  );
  const isMismatchedNetwork = computed(() => {
    return (
      isWalletReady.value &&
      userNetworkConfig.value?.key !== appNetworkConfig.key
    );
  });
  const isUnsupportedNetwork = computed(() => {
    return isWalletReady.value && userNetworkConfig.value === null;
  });
  const explorerLinks = {
    txLink: (txHash: string) =>
      `${configService.network.explorer}/tx/${txHash}`,
    addressLink: (address: string) =>
      `${configService.network.explorer}/address/${address}`,
    tokenLink: (address: string) =>
      `${configService.network.explorer}/token/${address}`,
  };

  // METHODS
  const Web3Provider = getWeb3Provider();
  const getProvider = () => new Web3Provider(provider.value as any, 'any'); // https://github.com/ethers-io/ethers.js/issues/866
  const getSigner = () => getProvider().getSigner();

  const connectToAppNetwork = () => {
    networkData.value.switchNetwork(
      chainId.value === BERACHAIN.id ? BEPOLIA : BERACHAIN
    );
  };

  function startConnectWithInjectedProvider(): void {
    connectWallet();
  }

  const { isLoading: isLoadingProfile, data: profile } = useQuery(
    QUERY_KEYS.Account.Profile(networkId, account, chainId),
    () => walletService.getProfile(account.value),
    reactive({
      enabled: canLoadProfile,
    })
  );

  return {
    // refs
    account,
    chainId,
    profile,
    // connector,
    walletInfo,
    provider,
    walletState,
    userNetworkConfig,
    appNetworkConfig,
    isLoadingProfile,
    isWalletReady,
    isMismatchedNetwork,
    isUnsupportedNetwork,
    explorerLinks,
    signer,
    blockNumber,
    isWalletConnecting,
    isBlocked: ref(false), // @todo: wallet whitelisting API to filter bad actors
    isWalletDisconnected,

    // methods
    connectWallet,
    connectToAppNetwork,
    getProvider,
    getSigner,
    disconnectWallet,
    toggleWalletSelectModal: connectWallet,
    startConnectWithInjectedProvider,
    setBlockNumber,
  };
}
